<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 mt-5">
        <div>
          <TableTitle
            title="Knowledge Articles List"
            pageTitle="Knowledge"
            icon="fas fa-book"
            :showAction="true"
            actionLabel="Create Article"
            @action="openCreateModal"
            :disabled="!canCreate('website')"
          />
          <div class="table_style">
            <CommonTable2
              :tableObj="tableConfig"
              @edit="editBlog"
              @delete="deleteBlog"
              @view="viewBlog"
              @toggleFeatured="toggleFeatured"
              @publish="publishBlog"
              @unPublish="unpublishBlog"
              @statusFilter="handleStatusFilter"
              @categoryFilter="handleCategoryFilter"
              @resetFilters="resetFilters"
              @page-changed="handlePageChange"
              permissionKey="website"
            />
          </div>

          <!-- pagination -->
          <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
            <!-- Rows per page -->
            <div class="d-flex align-items-center gap-2">
              <span class="text-muted">Rows per page:</span>
              <select
                class="form-select form-select-sm rows-select"
                v-model="tablePagination.per_page"
                @change="changeRows"
              >
                <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
                  {{ n }}
                </option>
              </select>
            </div>

            
            <!-- Pagination Buttons -->
            <div class="pagination-mini">
              <!-- Previous -->
              <button
                class="page-btn prev_btn"
                :disabled="!tablePagination.has_prev"
                @click="handlePageChange(tablePagination.current_page - 1)"
              >
                «
              </button>
              <!-- Numbered Pages -->
              <button
                v-for="(p, index) in visiblePages"
                :key="index"
                class="page-btn"
                :class="{
                  'page-active': p === tablePagination.current_page,
                  'disabled-dots': p === '...',
                }"
                :disabled="p === '...'"
                @click="p !== '...' && handlePageChange(p)"
              >
                {{ p }}
              </button>

              <!-- Next -->
              <button
                class="page-btn next_btn"
                :disabled="!tablePagination.has_next"
                @click="handlePageChange(tablePagination.current_page + 1)"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Knowledge Create/Edit Modal -->
    <KnowledgeModal
      v-if="showModal"
      :show="showModal"
      :article="selectedBlog"
      :categories="categories"
      @close="closeModal"
      @saved="handleBlogSaved"
    />

    <!-- Knowledge View Modal -->
    <KnowledgeViewModal
      v-if="showViewModal"
      :show="showViewModal"
      :article="selectedBlog"
      :categories="categories"
      @close="closeViewModal"
      @edit="editFromView"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  onMounted,
  watch,
  defineAsyncComponent,
} from "vue";
import { useRouter } from "vue-router";
import apiService from "@/services/apiService";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { toast } from "vue3-toastify";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();
const KnowledgeModal = defineAsyncComponent(
  () => import("@/components/theme/admin/knowledge/KnowledgeModal.vue")
);
const KnowledgeViewModal = defineAsyncComponent(
  () => import("@/components/theme/admin/knowledge/KnowledgeViewModal.vue")
);

const router = useRouter();
const loading = ref(false);
const blogs = ref([]);
const categories = ref([]);
const selectedBlog = ref(null);
const showModal = ref(false);
const showViewModal = ref(false);
const rows = ref([]);
const searchQuery = ref("");

interface Filters {
  status: string;
  category: string;
  is_featured: string;
}
// Filters
const filters = reactive<Filters>({
  status: "",
  category: "",
  is_featured: "",
});

// pagination
const currentPage = ref(1);
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  // If total pages small → show all
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let pages: any[] = [];

  // Always show first page
  pages.push(1);

  // Left side "..."
  if (current > 4) {
    pages.push("...");
  }

  // Middle window (current -1, current, current +1)
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);

  for (let i = windowStart; i <= windowEnd; i++) {
    pages.push(i);
  }

  // Right side "..."
  if (current < total - 3) {
    pages.push("...");
  }

  // Always show last page
  pages.push(total);

  return pages;
});
const changeRows = () => {
  currentPage.value = 1;
  fetchBlogs();
};
watch(
  () => tablePagination.value,
  (newVal) => {
    tableConfig.value.pagination = newVal;
  },
  { deep: true }
);

const resetFilters = () => {
  Object.keys(filters).forEach((key) => {
    (filters as any)[key] = "";
  });
  searchQuery.value = "";
  currentPage.value = 1; // RESET PAGE
};

// FETCH BLOGS (NO PAGINATION)
// Methods
const fetchBlogs = async () => {
  try {
    loading.value = true;
    const params: Record<string, any> = {
      page: currentPage.value,
      limit: tablePagination.value.per_page,
      search: searchQuery.value,
      ...filters,
    };

    // Remove empty filters
    Object.keys(params).forEach((key) => {
      if (
        params[key] === "" ||
        params[key] === null ||
        params[key] === undefined
      ) {
        delete params[key];
      }
    });

    const response = await apiService.getKnowledge(params);
    blogs.value = response.data || [];
    tablePagination.value = {
      current_page: response.pagination?.current_page || 1,
      per_page: params.limit,
      total: response.pagination?.total || 0,
      total_pages: response.pagination?.total_pages || 1,
      has_next: response.pagination?.has_next || false,
      has_prev: response.pagination?.has_prev || false,
    };
  } catch (error) {
    console.error("Error fetching blogs:", error);
    blogs.value = [];
  } finally {
    loading.value = false;
  }
};
// const fetchBlogs = async () => {
//   try {
//     loading.value = true;
//     const response = await apiService.getKnowledge();
//     blogs.value = response.data || [];
//   } catch (error) {
//     console.error("Error fetching blogs:", error);
//     blogs.value = [];
//   } finally {
//     loading.value = false;
//   }
// };

// FETCH CATEGORIES
const fetchCategories = async () => {
  try {
    const response = await apiService.getKnowledgeCategories({});
    categories.value = response.data || response || [];
    console.log(categories.value, "these are all categories");
  } catch (error) {
    categories.value = [];
  }
};

const openCreateModal = () => {
  selectedBlog.value = null;
  showModal.value = true;
};

const editBlog = (id: string) => {
  router.push(`/knowledge/${id}/edit`);
};

const viewBlog = (row: any) => {
  const article = blogs.value.find((b) => b._id === row._id);
  selectedBlog.value = article || row;
  showViewModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedBlog.value = null;
};

const closeViewModal = () => {
  showViewModal.value = false;
  selectedBlog.value = null;
};

const editFromView = (article: any) => {
  closeViewModal();
  router.push(`/knowledge/${article._id}/edit`);
};

const handleBlogSaved = () => {
  closeModal();
  fetchBlogs();
};

// Featured toggle
const toggleFeatured = async (id: string) => {
  try {
    const blog = blogs.value.find((b) => b._id === id);
    if (blog) blog.is_featured = !blog.is_featured;

    await apiService.toggleKnowledgeFeatured(id);
    toast.success(
      `Article ${ blog.is_featured ? "marked as featured" : "removed from featured"}`
    );
    fetchBlogs();
  } catch (error) {
    const blog = blogs.value.find((b) => b._id === id);
    if (blog) blog.is_featured = !blog.is_featured;
  }
};
const handleStatusFilter = (val: string) => {
  filters.status = val.toLowerCase();
  currentPage.value = 1; // RESET PAGE
};

const handleCategoryFilter = (id: string) => {
  filters.category = id;
  currentPage.value = 1; // RESET PAGE
};

const publishBlog = async (id: string) => {
  await apiService.publishKnowledge(id);
  fetchBlogs();
};

const unpublishBlog = async (id: string) => {
  await apiService.unpublishKnowledge(id);
  fetchBlogs();
};

const deleteBlog = async (id: string) => {
  if (confirm("Delete this article?")) {
    await apiService.deleteKnowledge(id);
    fetchBlogs();
  }
};

watch(
  [filters],
  () => {
    fetchBlogs();
  },
  { deep: true }
);

// Load on mount
onMounted(() => {
  fetchCategories();
  fetchBlogs();
});

// table config
const tableConfig = ref({
  columns: [
    { name: "Image", field: "featured_image", width: "auto" },
    { name: "Title", field: "title", sortable: true, width: "auto" },
    { name: "Category", field: "category", width: "auto" },
    { name: "Author", field: "author", width: "auto" },
    { name: "Status", field: "status", width: "auto" },
    { name: "Views", field: "views", width: "auto" },
    { name: "Featured", field: "featured", width: "auto" },
    { name: "Published", field: "published", width: "auto" },
  ],
  data: rows,
  actions: [true],
  loading: loading,
  categories: categories,
  pagination: tablePagination,
  showCategoryFilter: true,
  showStatusFilter: true,
  showResetFilter: true,
  table_width: "1500px",
  allStatusLables: ["Draft", "Published", "Scheduled", "Archived"],
});

const handlePageChange = (page) => {
  currentPage.value = page;
  fetchBlogs();
};

watch(
  blogs,
  (newVal) => {
    rows.value = newVal.map((item) => ({
      featured_image: item.featured_image,
      title: item.title,
      category: {
        id: item.category_id._id,
        title: item.category_id.title,
      },
      author: item.author?.name || "",
      published: item.published_at
        ? new Date(item.published_at).toLocaleDateString()
        : "Not published",
      views: {
        view_count: item.view_count,
        like_count: item.like_count,
        share_count: item.share_count,
      },
      status: item.status,
      _id: item._id,
      is_featured: item.is_featured,
      isBlog: true,
    }));
  },
  { deep: true }
);
</script>

<style scoped>
/* Remove custom table header styling to allow Bootstrap table-dark to work */
.table thead.table-dark th {
  background-color: var(--bs-dark) !important;
  color: var(--bs-white) !important;
  border-color: var(--bs-dark) !important;
  font-weight: 600;
}

.img-thumbnail {
  border: 1px solid #dee2e6;
}

.btn-group .btn {
  border-radius: 0.25rem;
  margin-right: 2px;
}

.badge {
  font-size: 0.75em;
}

.form-check-input {
  cursor: pointer;
}

.text-xs {
  font-size: 0.75rem;
}

/* Table responsiveness improvements */
.table-responsive {
  border-radius: 0.375rem;
  overflow: hidden;
}

.table {
  margin-bottom: 0;
}

.table th,
.table td {
  vertical-align: middle;
  padding: 0.75rem;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.025em;
}

/* Card styling improvements */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.table_style {
  overflow-x: auto;
}

/* pagination style */ 
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

/* Mini Pagination */
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0px;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Active page (highlight like the screenshot) */
.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}
.prev_btn,
.next_btn {
  color: #21c1d6;
}
.page-active:hover{
   background: #21c1d6;
}
</style>
